import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unblockSender } from "../../lib/api";
import { blockedQuery, qk } from "../../lib/queries";
import { relativeTime } from "../../lib/format";
import { useSessionExpired } from "../../lib/session";
import { LinkButton } from "../ui/button";
import { Sheet } from "../shell/surfaces";

export const BLOCKED_TITLE = "Blocked users";
export const BLOCKED_LEDE =
  "They cannot knock here, and their conversations are out of your lists. " +
  "Nothing was deleted — lifting a block puts it all back.";
export const BLOCKED_EMPTY = "You have not blocked anybody.";
export const BLOCKED_LOADING = "Reading your door.";
export const BLOCKED_FAILED = "Nob is not answering. Try that again.";
export const UNBLOCK_LABEL = "Unblock";

/**
 * Everyone this owner has shut out, with the way back in (FR-6.4).
 *
 * WHY IT IS A LIST AND NOT A LOG. Blocking is the one recipient action with no
 * expiry and no reminder: the knock leaves every surface, so after a week the
 * only evidence that a decision was made at all is that somebody is quietly not
 * getting through. A door whose owner cannot enumerate who it refuses is a door
 * they have stopped understanding, which is why this exists next to the
 * takedown rather than nowhere.
 *
 * WHAT IT SHOWS, AND WHAT IT DOES NOT. A handle, a name and when — the three
 * things that let the owner recognise a person they met once. No reason, because
 * the product never asked for one; no knock, because a block is about the
 * person, not the message they sent; and no count of what they tried since,
 * because a blocked sender's later attempts are not something to dangle.
 */
export function BlockedListDialog({ onClose }: { onClose: () => void }) {
  const sessionExpired = useSessionExpired();
  const queryClient = useQueryClient();
  const blocked = useQuery({ ...blockedQuery(), retry: false });
  const [failed, setFailed] = useState(false);

  const lift = useMutation({
    mutationFn: async (blockId: string) => unblockSender({ block_id: blockId }),
    onSuccess: (result) => {
      if (result.ok) {
        setFailed(false);
        void queryClient.invalidateQueries({ queryKey: qk.blocked() });
        // Their conversation comes back with them — the inbox excluded it while
        // the block stood, and nothing else would put it on screen again. The
        // threads are the list the console actually draws (§6.3).
        void queryClient.invalidateQueries({ queryKey: ["inbox"] });
        void queryClient.invalidateQueries({ queryKey: qk.threads() });
        return;
      }
      if (result.code === "UNAUTHENTICATED") {
        sessionExpired();
        return;
      }
      // A block that is already gone is the outcome the button wanted, so the
      // list is re-read rather than the failure reported.
      if (result.code === "BLOCK_NOT_FOUND") {
        void queryClient.invalidateQueries({ queryKey: qk.blocked() });
        return;
      }
      setFailed(true);
    },
    onError: () => setFailed(true),
  });

  const rows = blocked.data ?? [];

  return (
    <Sheet title={BLOCKED_TITLE} lede={BLOCKED_LEDE} onClose={onClose}>
      {blocked.isPending && <p className="nob-breathe text-sm text-faint">{BLOCKED_LOADING}</p>}
      {blocked.isError && (
        <p className="text-sm text-faint" role="alert">
          {BLOCKED_FAILED}
        </p>
      )}
      {!blocked.isPending && !blocked.isError && rows.length === 0 && (
        <p className="text-sm text-muted">{BLOCKED_EMPTY}</p>
      )}
      {rows.length > 0 && (
        <ul className="list-none">
          {rows.map((row) => (
            <li
              key={row.block_id}
              className="flex items-center gap-3 border-b border-border-soft py-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium">{row.display_name}</p>
                <p className="truncate text-[12px] text-faint">
                  {[row.handle ? `@${row.handle}` : "", relativeTime(row.blocked_at)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <LinkButton
                type="button"
                aria-label={`${UNBLOCK_LABEL} ${row.display_name}`}
                disabled={lift.isPending}
                onClick={() => lift.mutate(row.block_id)}
              >
                {UNBLOCK_LABEL}
              </LinkButton>
            </li>
          ))}
        </ul>
      )}
      {failed && (
        <p className="mt-3 text-[13px] text-faint" role="alert">
          {BLOCKED_FAILED}
        </p>
      )}
    </Sheet>
  );
}
